// ChatTasksRail — rail de TASKS da superfície Chat (recuperação Aba-Apolo C3).
//
// Lê o plano POR-SESSÃO do MESMO store que o SessionWorkspace alimenta no
// caminho vivo (planStateStore: PlanCard propose/approve + run_result) —
// título + todos com status (pending/active/done/blocked). Checklist + barra
// de progresso, flat e cinza-neutro. Dado real: nada de mock — vazio explica
// como popular (modo Plan). Só em telas largas (hidden xl:flex); o TasksPanel
// completo do SessionWorkspace continua opt-in pelo rail de botões.
//
// Adaptação vs órfão: token `--cx-success` não existe em main → apolo-success.

import { AlertTriangle, Check, Circle, ListChecks, Loader2 } from 'lucide-react';
import { usePlanState } from '../../../lib/planStateStore.js';

const STATUS = {
  done: { Icon: Check, cls: 'text-apolo-success' },
  active: { Icon: Loader2, cls: 'text-apolo-accent', spin: true },
  blocked: { Icon: AlertTriangle, cls: 'text-apolo-error' },
  pending: { Icon: Circle, cls: 'text-apolo-textTertiary' },
};

export default function ChatTasksRail({ sessionId }) {
  const plan = usePlanState(sessionId);
  const todos = Array.isArray(plan?.todos) ? plan.todos : [];
  const total = todos.length;
  const done = todos.filter((t) => t.status === 'done').length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <aside className="hidden w-[240px] shrink-0 flex-col gap-3 border-l border-apolo-border bg-apolo-surface1 px-3 py-3 xl:flex">
      <div className="flex items-center justify-between">
        <span className="cx-eyebrow">Tasks</span>
        {total > 0 && (
          <span className="font-mono text-[10px] text-apolo-textTertiary">{done}/{total}</span>
        )}
      </div>

      {total === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-3 text-center">
          <ListChecks size={20} aria-hidden="true" className="text-apolo-textTertiary" />
          <p className="text-[11px] leading-relaxed text-apolo-textTertiary">
            Sem tasks ainda. Use o modo <span className="text-apolo-textSecondary">Plan</span> ou peça
            um plano — os passos aparecem aqui com status.
          </p>
        </div>
      ) : (
        <>
          <div className="h-[3px] overflow-hidden rounded-full bg-apolo-surface3">
            <div
              className="h-full bg-apolo-accent transition-all"
              style={{ width: `${pct}%` }}
              aria-hidden="true"
            />
          </div>
          {plan.title ? (
            <div className="truncate text-xs font-medium text-apolo-textPrimary" title={plan.title}>
              {plan.title}
            </div>
          ) : null}
          <ul className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto" aria-label="Tasks do plano">
            {todos.map((t) => {
              const st = STATUS[t.status] || STATUS.pending;
              const Icon = st.Icon;
              const isDone = t.status === 'done';
              const isActive = t.status === 'active';
              return (
                <li
                  key={t.id}
                  className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 ${
                    isActive
                      ? 'border-apolo-accentBorder bg-apolo-accentMuted'
                      : 'border-apolo-border bg-apolo-surface2'
                  }`}
                >
                  <Icon
                    size={14}
                    aria-hidden="true"
                    className={`mt-px shrink-0 ${st.cls} ${st.spin ? 'animate-spin' : ''}`}
                  />
                  <div className="min-w-0">
                    <div
                      className={`text-xs leading-snug ${
                        isDone ? 'text-apolo-textTertiary line-through' : 'text-apolo-textPrimary'
                      }`}
                    >
                      {t.text}
                    </div>
                    {isActive && <div className="mt-0.5 text-[10px] text-apolo-accent">em andamento</div>}
                    {t.status === 'blocked' && (
                      <div className="mt-0.5 text-[10px] text-apolo-error">bloqueado</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </aside>
  );
}
